import { useEffect, useMemo, useState } from 'react';
import { Plus, AlertTriangle, ArrowDownToLine, ArrowUpFromLine } from 'lucide-react';
import { Button, Card, Input, Modal, Pill, useToast } from '@nako/ui';
import { formatMoney } from '@nako/core';
import { useAuthStore } from '@/stores/auth';
import { useRegionStore } from '@/stores/region';
import { supabase } from '@/lib/supabase';
import { postSale, postReceive, postOpeningStock } from '@/lib/inventory-postings';
import { iconsForIndustry } from '@/lib/industry-icons';

interface InventoryItem {
  id: string;
  business_id: string;
  sku: string | null;
  name: string;
  unit: string;
  cost_price: number | null;
  sale_price: number | null;
  qty_on_hand: number;
  reorder_point: number;
  is_active: boolean;
}

type Movement = { type: 'sale' | 'receive'; item: InventoryItem };

export default function Inventory() {
  const business = useAuthStore((s) => s.business)!;
  const session = useAuthStore((s) => s.session);
  const regionCode = useRegionStore((s) => s.code);
  const toast = useToast();
  const Icon = iconsForIndustry(business.industry).inventory;
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [filter, setFilter] = useState<'all' | 'low' | 'out'>('all');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    sku: '',
    name: '',
    unit: 'each',
    qty: 0,
    reorder: 0,
    cost: 0,
    price: 0,
  });

  // Stock movement modal (sell / receive)
  const [movement, setMovement] = useState<Movement | null>(null);
  const [movQty, setMovQty] = useState(1);
  const [movPrice, setMovPrice] = useState(0);
  const [movBusy, setMovBusy] = useState(false);

  useEffect(() => {
    void load();
  }, [business.id]);

  async function load() {
    const { data } = await supabase
      .from('inventory_items')
      .select('*')
      .eq('business_id', business.id)
      .order('name', { ascending: true });
    setItems((data ?? []) as InventoryItem[]);
  }

  async function create() {
    if (!form.name) {
      toast.show('Item name required', { tone: 'warning' });
      return;
    }
    const qty = Number(form.qty) || 0;
    const cost = Number(form.cost) || 0;
    const { data: inserted, error } = await supabase
      .from('inventory_items')
      .insert({
        business_id: business.id,
        sku: form.sku || null,
        name: form.name,
        unit: form.unit || 'each',
        cost_price: cost > 0 ? cost : null,
        sale_price: Number(form.price) > 0 ? Number(form.price) : null,
        qty_on_hand: qty,
        reorder_point: Number(form.reorder) || 0,
        is_active: true,
      })
      .select()
      .single();

    if (error || !inserted) {
      toast.show('Could not add item — please try again.', { tone: 'error' });
      return;
    }

    // Opening stock entered with a cost → debit Inventory / credit Owner's
    // Capital, so the first sale's COGS release nets correctly.
    let openingPosted = false;
    if (qty > 0 && cost > 0) {
      const r = await postOpeningStock({
        item: inserted as InventoryItem,
        qty,
        unitCost: cost,
        user_id: session?.user.id ?? null,
      });
      openingPosted = r.ok && Boolean(r.journal_entry_id);
    }

    toast.show(
      openingPosted
        ? `Item added. Opening stock posted to ledger (Inventory, Owner's Capital).`
        : 'Item added.',
      { tone: 'success' },
    );
    setOpen(false);
    setForm({ sku: '', name: '', unit: 'each', qty: 0, reorder: 0, cost: 0, price: 0 });
    void load();
  }

  async function adjust(item: InventoryItem, delta: number) {
    const newQty = Math.max(0, item.qty_on_hand + delta);
    await supabase.from('inventory_items').update({ qty_on_hand: newQty }).eq('id', item.id);
    void load();
  }

  function openMovement(type: 'sale' | 'receive', item: InventoryItem) {
    setMovement({ type, item });
    setMovQty(1);
    setMovPrice(type === 'sale' ? (item.sale_price ?? 0) : (item.cost_price ?? 0));
  }

  function closeMovement() {
    setMovement(null);
    setMovQty(1);
    setMovPrice(0);
  }

  async function submitMovement() {
    if (!movement) return;
    setMovBusy(true);
    const { item, type } = movement;
    const userId = session?.user.id ?? null;

    if (type === 'sale') {
      const totalPrice = movQty * movPrice;
      const r = await postSale({
        item,
        qty: movQty,
        totalPrice,
        user_id: userId,
        vat_registered: Boolean((business as { vat_registered?: boolean }).vat_registered),
      });
      if (!r.ok) {
        toast.show(r.error ?? 'Sale failed', { tone: 'error' });
      } else {
        toast.show(
          item.cost_price
            ? `Sold ${movQty} × ${item.name}. Stock - ${movQty}, ledger updated (Cash, Sales, COGS, Inventory).`
            : `Sold ${movQty} × ${item.name}. Stock - ${movQty}, Cash and Sales posted.`,
          { tone: 'success' },
        );
      }
    } else {
      const r = await postReceive({
        item,
        qty: movQty,
        unitCost: movPrice,
        user_id: userId,
      });
      if (!r.ok) {
        toast.show(r.error ?? 'Receipt failed', { tone: 'error' });
      } else {
        toast.show(
          `Received ${movQty} × ${item.name}. Stock + ${movQty}, Inventory + Cash posted.`,
          { tone: 'success' },
        );
      }
    }

    setMovBusy(false);
    closeMovement();
    void load();
  }

  const filtered = useMemo(() => {
    if (filter === 'low')
      return items.filter((i) => i.qty_on_hand > 0 && i.qty_on_hand <= i.reorder_point);
    if (filter === 'out') return items.filter((i) => i.qty_on_hand === 0);
    return items;
  }, [items, filter]);

  const counters = useMemo(
    () => ({
      total: items.length,
      low: items.filter((i) => i.qty_on_hand > 0 && i.qty_on_hand <= i.reorder_point).length,
      out: items.filter((i) => i.qty_on_hand === 0).length,
    }),
    [items],
  );

  return (
    <main className="mx-auto max-w-3xl px-4 py-4 md:px-6 md:py-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-display flex items-center gap-2 text-2xl">
          <Icon size={22} /> Inventory
        </h1>
        <Button size="sm" leftIcon={<Plus size={16} />} onClick={() => setOpen(true)}>
          Add item
        </Button>
      </div>

      {/* Counters */}
      <div className="mb-4 grid grid-cols-3 gap-2">
        <Counter label="Items" value={counters.total} tone="ink" />
        <Counter label="Low stock" value={counters.low} tone="accent" />
        <Counter label="Out" value={counters.out} tone="red" />
      </div>

      {/* Filters */}
      <div className="mb-4 flex gap-2">
        {(['all', 'low', 'out'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`rounded-pill border px-3 py-1.5 text-xs font-medium ${
              filter === f ? 'bg-ink text-paper border-ink' : 'border-line text-muted bg-white'
            }`}
          >
            {f === 'all' ? 'All' : f === 'low' ? 'Low stock' : 'Out of stock'}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <Card className="py-10 text-center">
          <Icon size={32} className="text-muted mx-auto mb-3 opacity-50" />
          <p className="text-muted mb-4">
            {items.length === 0
              ? 'No inventory items yet.'
              : `No items match the "${filter}" filter.`}
          </p>
          {items.length === 0 && (
            <Button onClick={() => setOpen(true)} leftIcon={<Plus size={16} />}>
              Add your first item
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((item) => {
            const isOut = item.qty_on_hand === 0;
            const isLow = !isOut && item.qty_on_hand <= item.reorder_point;
            return (
              <Card key={item.id} compact className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{item.name}</p>
                  <p className="text-muted truncate text-xs">
                    {item.sku && <span className="font-mono">{item.sku}</span>}
                    {item.sku && ' · '}
                    {item.unit}
                    {item.reorder_point > 0 && ` · reorder ≤ ${item.reorder_point}`}
                  </p>
                </div>
                <div className="ml-3 flex items-center gap-2">
                  {isOut && (
                    <Pill tone="red">
                      <AlertTriangle size={10} className="mr-1 inline" />
                      out
                    </Pill>
                  )}
                  {isLow && (
                    <Pill tone="accent">
                      <AlertTriangle size={10} className="mr-1 inline" />
                      low
                    </Pill>
                  )}
                  <button
                    onClick={() => openMovement('receive', item)}
                    className="bg-blue/10 hover:bg-blue/20 text-blue rounded-btn flex h-8 items-center gap-1 px-2 text-xs font-medium"
                    aria-label="Receive stock"
                    title="Buy / receive stock"
                  >
                    <ArrowDownToLine size={12} /> In
                  </button>
                  <button
                    onClick={() => openMovement('sale', item)}
                    disabled={isOut}
                    className="bg-green/10 hover:bg-green/20 text-green rounded-btn flex h-8 items-center gap-1 px-2 text-xs font-medium disabled:opacity-40"
                    aria-label="Sell stock"
                    title="Sell to customer"
                  >
                    <ArrowUpFromLine size={12} /> Sell
                  </button>
                  <button
                    onClick={() => adjust(item, -1)}
                    className="bg-paper-2 hover:bg-line h-8 w-8 rounded-full text-sm font-bold"
                    aria-label="Decrease"
                  >
                    −
                  </button>
                  <p className="tnum w-12 text-right font-mono text-sm">{item.qty_on_hand}</p>
                  <button
                    onClick={() => adjust(item, +1)}
                    className="bg-paper-2 hover:bg-line h-8 w-8 rounded-full text-sm font-bold"
                    aria-label="Increase"
                  >
                    +
                  </button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Add inventory item">
        <div className="space-y-3">
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <Input
            label="SKU (optional)"
            value={form.sku}
            onChange={(e) => setForm({ ...form, sku: e.target.value })}
          />
          <Input
            label="Unit"
            placeholder="each, kg, case, bottle…"
            value={form.unit}
            onChange={(e) => setForm({ ...form, unit: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Cost price (per unit)"
              type="number"
              numeric
              leftAddon="P"
              value={form.cost || ''}
              onChange={(e) => setForm({ ...form, cost: Number(e.target.value) || 0 })}
            />
            <Input
              label="Sale price (per unit)"
              type="number"
              numeric
              leftAddon="P"
              value={form.price || ''}
              onChange={(e) => setForm({ ...form, price: Number(e.target.value) || 0 })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Qty on hand"
              type="number"
              numeric
              value={form.qty || ''}
              onChange={(e) => setForm({ ...form, qty: Number(e.target.value) || 0 })}
            />
            <Input
              label="Reorder point"
              type="number"
              numeric
              value={form.reorder || ''}
              onChange={(e) => setForm({ ...form, reorder: Number(e.target.value) || 0 })}
            />
          </div>
          {form.cost > 0 && form.qty > 0 && (
            <p className="text-muted text-xs">
              Opening stock {formatMoney(form.cost * form.qty, regionCode)} will be posted to the
              ledger (Dr Inventory / Cr Owner&apos;s Capital).
            </p>
          )}
          <Button full onClick={create}>
            Save
          </Button>
        </div>
      </Modal>

      {/* Sell / Receive movement modal */}
      <Modal
        open={movement !== null}
        onClose={closeMovement}
        title={
          movement
            ? movement.type === 'sale'
              ? `Sell ${movement.item.name}`
              : `Receive ${movement.item.name}`
            : ''
        }
      >
        {movement && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submitMovement();
            }}
            className="space-y-3"
          >
            <p className="text-muted text-xs">
              {movement.type === 'sale' ? 'Selling to customer' : 'Buying from supplier'} ·{' '}
              <span className="font-mono">{movement.item.sku ?? '—'}</span> · on hand{' '}
              <span className="font-mono">{movement.item.qty_on_hand}</span> {movement.item.unit}
            </p>

            <div className="grid grid-cols-2 gap-3">
              <Input
                label={`Qty (${movement.item.unit})`}
                type="number"
                numeric
                value={movQty || ''}
                min="1"
                onChange={(e) => setMovQty(Number(e.target.value) || 0)}
                autoFocus
              />
              <Input
                label={movement.type === 'sale' ? 'Sale price (per unit)' : 'Cost price (per unit)'}
                type="number"
                numeric
                leftAddon="P"
                value={movPrice || ''}
                onChange={(e) => setMovPrice(Number(e.target.value) || 0)}
              />
            </div>

            {/* Total preview */}
            <div className="bg-paper-2 rounded-card flex items-center justify-between p-3">
              <span className="text-muted text-sm">Total</span>
              <span className="tnum font-mono text-lg font-semibold">
                {formatMoney(movQty * movPrice, regionCode)}
              </span>
            </div>

            {/* Bundled-consumable preview (e.g. clays for ammo) */}
            {movement.type === 'sale' &&
              (
                movement.item as InventoryItem & {
                  bundled_sku?: string | null;
                  bundled_qty?: number | null;
                }
              ).bundled_sku && (
                <div className="bg-blue/10 text-blue rounded-card p-3 text-xs">
                  <p className="font-semibold">Bundled consumable</p>
                  <p>
                    {movQty} × {movement.item.unit}{' '}
                    {(movement.item as InventoryItem & { bundled_qty?: number | null })
                      .bundled_qty ?? 0}{' '}
                    ={' '}
                    <span className="font-mono">
                      {movQty *
                        ((movement.item as InventoryItem & { bundled_qty?: number | null })
                          .bundled_qty ?? 0)}
                    </span>{' '}
                    units of{' '}
                    {(movement.item as InventoryItem & { bundled_sku?: string | null }).bundled_sku}{' '}
                    will be drawn down automatically.
                  </p>
                </div>
              )}

            {/* Supplier picker for receive */}
            {movement.type === 'receive' && (
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium">Supplier</label>
                <select
                  className="min-h-tap rounded-btn border-line border bg-white px-3"
                  defaultValue="PAAM"
                  onChange={() => {
                    /* supplier choice currently informational; will be saved
                     * once Suppliers master is wired up */
                  }}
                >
                  <option value="PAAM">PAAM</option>
                  <option value="Imports">Imports</option>
                  <option value="Explosion">Explosion</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            )}

            {/* Journal-posting preview */}
            <div className="text-muted bg-paper-2 rounded-card p-3 text-xs">
              <p className="mb-1 font-semibold">Will post to ledger:</p>
              {movement.type === 'sale' ? (
                <ul className="list-disc pl-4">
                  <li>Dr Cash {formatMoney(movQty * movPrice, regionCode)}</li>
                  <li>Cr Sales Revenue {formatMoney(movQty * movPrice, regionCode)}</li>
                  {movement.item.cost_price && movement.item.cost_price > 0 && (
                    <>
                      <li>
                        Dr Cost of Sales{' '}
                        {formatMoney(movement.item.cost_price * movQty, regionCode)}
                      </li>
                      <li>
                        Cr Inventory {formatMoney(movement.item.cost_price * movQty, regionCode)}
                      </li>
                    </>
                  )}
                </ul>
              ) : (
                <ul className="list-disc pl-4">
                  <li>Dr Inventory {formatMoney(movQty * movPrice, regionCode)}</li>
                  <li>Cr Cash {formatMoney(movQty * movPrice, regionCode)}</li>
                </ul>
              )}
            </div>

            <Button
              type="submit"
              full
              loading={movBusy}
              variant={movement.type === 'sale' ? 'primary' : 'secondary'}
            >
              {movement.type === 'sale' ? 'Confirm sale' : 'Confirm receipt'}
            </Button>
            <button
              type="button"
              onClick={closeMovement}
              className="text-muted hover:text-ink mx-auto block text-xs"
            >
              Cancel
            </button>
          </form>
        )}
      </Modal>
    </main>
  );
}

function Counter({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'ink' | 'accent' | 'red';
}) {
  const colour: Record<string, string> = {
    ink: 'text-ink',
    accent: 'text-accent',
    red: 'text-red',
  };
  return (
    <Card compact>
      <p className="text-muted text-xs">{label}</p>
      <p className={`tnum font-mono text-2xl ${colour[tone]}`}>{value}</p>
    </Card>
  );
}
